import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Grid,
  Box,
  Typography,
  Checkbox,
  FormControlLabel,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText,
  Divider,
  Switch,
  IconButton,
  Tooltip,
  CircularProgress,
  useTheme,
  alpha,
} from '@mui/material';
import { Close as CloseIcon, Edit as EditIcon } from '@mui/icons-material';
import { toast } from 'react-toastify';

import { contractService } from '../../services/contractService';
import GatewaySelect from '../selects/GatewaySelect';
import FuncionariosSelect from '../selects/FuncionariosSelect';

interface BulkEditContractsDialogProps {
  open: boolean;
  onClose: () => void;
  selectedIds: number[];
  onSuccess: () => void;
}

const BulkEditContractsDialog: React.FC<BulkEditContractsDialogProps> = ({
  open,
  onClose,
  selectedIds,
  onSuccess,
}) => {
  const theme = useTheme();
  const [loading, setLoading] = useState(false);
  
  const initialValues = {
    dia_vencimento: 10,
    fidelidade: 12,
    isento: false,
    sms_lembrete: true,
    gerar_fatura_automatico: true,
    geranfcom: false,
    pgcorte: true,
    dias_corte: 5,
    acrescimo: 0,
    desconto: 0,
    gateway_id: '',
    cfop: '5307',
    modalidade: 'pos-pago',
    vendedor_id: '',
  };

  // Estado para os valores dos campos
  const [formData, setFormData] = useState<any>(initialValues);
  
  // Estado para controlar quais campos foram modificados
  const [modifiedFields, setModifiedFields] = useState<Set<string>>(new Set());
  const handleInputChange = (field: string, value: any) => {
    setFormData((prev: any) => ({ ...prev, [field]: value }));
    setModifiedFields(prev => {
      const next = new Set(prev);
      next.add(field);
      return next;
    });
  };

  // Resetar estado quando o modal abre
  useEffect(() => {
    if (open) {
      setFormData(initialValues);
      setModifiedFields(new Set());
    }
  }, [open]);

  const handleSave = async () => {
    const dataToUpdate: any = {};
    let hasField = false;

    modifiedFields.forEach(field => {
      dataToUpdate[field] = formData[field];
      hasField = true;
    });

    if (!hasField) {
      toast.warning('Selecione ao menos um campo para editar');
      return;
    }

    setLoading(true);
    try {
      const response = await contractService.bulkUpdate(selectedIds, dataToUpdate);
      if (response.success) {
        toast.success(`Sucesso: ${response.message}`);
        onSuccess();
        onClose();
        // Reset state
        setModifiedFields(new Set());
        setFormData(initialValues);
      } else {
        toast.error(response.message || 'Erro ao realizar atualização em massa');
      }
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Erro de conexão com o servidor');
    } finally {
      setLoading(false);
    }
  };

  const renderField = (
    label: string,
    field: string,
    component: React.ReactNode,
    xs = 12,
    md = 6
  ) => {
    const isModified = modifiedFields.has(field);
    return (
      <Grid item xs={xs} md={md}>
        <Box sx={{ 
          p: 1.5, 
          borderRadius: 1.5, 
          border: '1px solid',
          borderColor: isModified ? 'primary.main' : 'divider',
          bgcolor: isModified ? alpha(theme.palette.primary.main, 0.02) : 'transparent',
          position: 'relative',
          transition: 'all 0.2s',
          '&:hover': { borderColor: isModified ? 'primary.dark' : 'grey.400' }
        }}>
          {isModified && (
            <Typography 
              variant="caption" 
              sx={{ 
                position: 'absolute', 
                top: -8, 
                left: 12, 
                bgcolor: 'primary.main', 
                color: 'white', 
                px: 1, 
                borderRadius: 1,
                fontSize: '0.65rem',
                fontWeight: 'bold',
                zIndex: 1
              }}
            >
              MODIFICADO
            </Typography>
          )}
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5, fontWeight: 600 }}>
            {label}
          </Typography>
          {component}
        </Box>
      </Grid>
    );
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <EditIcon color="primary" />
          <Typography variant="h6">Edição em Massa - {selectedIds.length} contrato(s)</Typography>
        </Box>
        <IconButton onClick={onClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <Divider />
      <DialogContent sx={{ py: 3 }}>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Altere os valores abaixo. Campos que forem modificados serão atualizados em todos os contratos selecionados.
        </Typography>

        <Grid container spacing={3}>
          {renderField('Dia de Vencimento', 'dia_vencimento', (
            <FormControl fullWidth size="small">
              <InputLabel>Dia de Vencimento</InputLabel>
              <Select
                value={formData.dia_vencimento}
                onChange={(e) => handleInputChange('dia_vencimento', e.target.value)}
                label="Dia de Vencimento"
              >
                {Array.from({ length: 31 }, (_, i) => i + 1).map(d => (
                  <MenuItem key={d} value={d}>Dia {d}</MenuItem>
                ))}
              </Select>
            </FormControl>
          ))}

          {renderField('Fidelidade (meses)', 'fidelidade', (
            <TextField
              fullWidth
              size="small"
              label="Fidelidade (meses)"
              type="number"
              value={formData.fidelidade}
              onChange={(e) => handleInputChange('fidelidade', parseInt(e.target.value))}
            />
          ))}

          {renderField('Status Isento', 'isento', (
            <FormControlLabel
              control={
                <Switch
                  checked={formData.isento}
                  onChange={(e) => handleInputChange('isento', e.target.checked)}
                />
              }
              label={formData.isento ? "Sim" : "Não"}
            />
          ))}

          {renderField('SMS Lembrete', 'sms_lembrete', (
            <FormControlLabel
              control={
                <Switch
                  checked={formData.sms_lembrete}
                  onChange={(e) => handleInputChange('sms_lembrete', e.target.checked)}
                />
              }
              label={formData.sms_lembrete ? "Ativado" : "Desativado"}
            />
          ))}

          {renderField('Gerar Fatura Automático', 'gerar_fatura_automatico', (
            <FormControlLabel
              control={
                <Switch
                  checked={formData.gerar_fatura_automatico}
                  onChange={(e) => handleInputChange('gerar_fatura_automatico', e.target.checked)}
                />
              }
              label={formData.gerar_fatura_automatico ? "Ativado" : "Desativado"}
            />
          ))}

          {renderField('Gerar NFCom', 'geranfcom', (
            <FormControlLabel
              control={
                <Switch
                  checked={formData.geranfcom}
                  onChange={(e) => handleInputChange('geranfcom', e.target.checked)}
                />
              }
              label={formData.geranfcom ? "Sim" : "Não"}
            />
          ))}

          {renderField('Corte Automático (pgcorte)', 'pgcorte', (
            <FormControlLabel
              control={
                <Switch
                  checked={formData.pgcorte}
                  onChange={(e) => handleInputChange('pgcorte', e.target.checked)}
                />
              }
              label={formData.pgcorte ? "Sim" : "Não"}
            />
          ))}

          {renderField('Dias para Corte', 'dias_corte', (
            <TextField
              fullWidth
              size="small"
              label="Dias para Corte"
              type="number"
              value={formData.dias_corte}
              onChange={(e) => handleInputChange('dias_corte', parseInt(e.target.value))}
            />
          ))}

          {renderField('Acréscimo (%)', 'acrescimo', (
            <TextField
              fullWidth
              size="small"
              label="Acréscimo (%)"
              type="number"
              value={formData.acrescimo}
              onChange={(e) => handleInputChange('acrescimo', parseFloat(e.target.value))}
            />
          ))}

          {renderField('Desconto (%)', 'desconto', (
            <TextField
              fullWidth
              size="small"
              label="Desconto (%)"
              type="number"
              value={formData.desconto}
              onChange={(e) => handleInputChange('desconto', parseFloat(e.target.value))}
            />
          ))}

          {renderField('Gateway de Cobrança', 'gateway_id', (
             <GatewaySelect
               value={formData.gateway_id || null}
               onChange={(val) => handleInputChange('gateway_id', val)}
               size="small"
             />
          ))}

          {renderField('CFOP', 'cfop', (
            <TextField
              fullWidth
              size="small"
              label="CFOP"
              value={formData.cfop}
              onChange={(e) => handleInputChange('cfop', e.target.value)}
            />
          ))}

          {renderField('Modalidade', 'modalidade', (
            <FormControl fullWidth size="small">
              <InputLabel>Modalidade</InputLabel>
              <Select
                value={formData.modalidade}
                onChange={(e) => handleInputChange('modalidade', e.target.value)}
                label="Modalidade"
              >
                <MenuItem value="pos-pago">
                  Pós-pago{' '}
                  <Box component="span" sx={{ fontStyle: 'italic', color: 'text.secondary', ml: 0.5 }}>
                    - Paga depois de usar
                  </Box>
                </MenuItem>
                <MenuItem value="pre-pago">
                  Pré-pago{' '}
                  <Box component="span" sx={{ fontStyle: 'italic', color: 'text.secondary', ml: 0.5 }}>
                    - Paga antes de usar
                  </Box>
                </MenuItem>
              </Select>
            </FormControl>
          ))}

          {renderField('Vendedor', 'vendedor_id', (
            <FuncionariosSelect
              value={formData.vendedor_id || null}
              onChange={(val) => handleInputChange('vendedor_id', val)}
              size="small"
              label="Vendedor"
              placeholder="Selecione o Vendedor"
              allowClear
            />
          ))}
        </Grid>
      </DialogContent>
      <Divider />
      <DialogActions sx={{ p: 2, bgcolor: 'grey.50' }}>
        <Button onClick={onClose} color="inherit" disabled={loading}>
          Cancelar
        </Button>
        <Button
          onClick={handleSave}
          variant="contained"
          disabled={loading || modifiedFields.size === 0}
          startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <SaveIcon />}
        >
          {loading ? 'Salvando...' : 'Atualizar Contratos'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// Icones adicionais
const SaveIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z"></path>
    <polyline points="17 21 17 13 7 13 7 21"></polyline>
    <polyline points="7 3 7 8 15 8"></polyline>
  </svg>
);

export default BulkEditContractsDialog;
